// biome-ignore-all lint/a11y/useSemanticElements: this is a virtualized list —
// <ul>/<li> can't be used because absolute-positioned row wrappers and the CSS
// grid sit between the list container and its items, so div + role="list"/
// "listitem" (with presentation on the intermediate layout nodes) is the
// correct, standard shape for windowed lists.
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type ColumnBreakpoint,
  useElementColumns,
} from "../hooks/use-element-columns";

/** A group of cards with an optional full-width header rendered above them. */
export type CardSection<Item> = {
  /** Stable id — a server name, or any stable key for a header-less section. */
  id: string;
  /** Full-width header row (e.g. a toolkit group header). Omit for a flat grid. */
  header?: ReactNode;
  items: Item[];
};

// A section flattens into one optional header row plus one row per column-chunk
// of its cards, so the whole picker body — flat or grouped — is a single 1-D
// list the virtualizer can window. This keeps one virtualizer per scroll
// container (no stacking, so no scroll-margin coordination) and scales to
// thousands of tools: only the rows in view are ever in the DOM.
type Row<Item> =
  | { kind: "header"; key: string; node: ReactNode }
  | {
      kind: "cards";
      key: string;
      items: Item[];
      startIndex: number;
      /** An extra, non-item cell appended after the cards in this row. */
      trailingCard?: ReactNode;
    };

const GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

// Below this item count the list renders plainly: it's cheap, skips the
// virtualizer's overhead, and works where there's no layout to measure (jsdom
// tests). Only longer lists — a big server's tool list, a large catalog — are
// windowed.
const VIRTUALIZE_THRESHOLD = 24;

export function buildRows<Item>(
  sections: CardSection<Item>[],
  columns: number,
  getItemKey: (item: Item) => string,
  trailingCard?: ReactNode
): Row<Item>[] {
  const rows: Row<Item>[] = [];
  // Running 0-based position across every card, so each card can carry its
  // aria-posinset within the whole flattened list (not just its group).
  let itemIndex = 0;
  for (const section of sections) {
    if (section.header !== undefined) {
      rows.push({
        kind: "header",
        key: `header:${section.id}`,
        node: section.header,
      });
    }
    for (let i = 0; i < section.items.length; i += columns) {
      const items = section.items.slice(i, i + columns);
      rows.push({
        kind: "cards",
        key: `cards:${getItemKey(items[0])}`,
        items,
        startIndex: itemIndex,
      });
      itemIndex += items.length;
    }
  }
  // The trailing card flows as one more grid cell rather than a full-width
  // banner: it fills the empty column of the last card row when there is one,
  // otherwise it starts its own row. Either way it stays card-sized and next to
  // the last result.
  if (trailingCard !== undefined) {
    const last = rows.at(-1);
    if (last?.kind === "cards" && last.items.length < columns) {
      last.trailingCard = trailingCard;
    } else {
      rows.push({
        kind: "cards",
        key: "cards:trailing",
        items: [],
        startIndex: itemIndex,
        trailingCard,
      });
    }
  }
  return rows;
}

export function VirtualizedCardGrid<Item>({
  cardRowHeight,
  columnBreakpoints,
  gapPx = 8,
  getItemKey,
  headerHeight = 0,
  label,
  renderItem,
  scrollContainer,
  sections,
  trailingCard,
}: {
  /** Estimated height of one row of cards; estimate the tallest case (see docs). */
  cardRowHeight: number;
  columnBreakpoints: ColumnBreakpoint[];
  gapPx?: number;
  getItemKey: (item: Item) => string;
  /** Estimated height of a section header row. Only needed for grouped grids. */
  headerHeight?: number;
  /** Accessible name for the list (e.g. "MCP servers", "Tools"). */
  label?: string;
  renderItem: (item: Item) => ReactNode;
  /** Scroll viewport. While null the grid renders every row (no windowing). */
  scrollContainer: HTMLElement | null;
  sections: CardSection<Item>[];
  /** A card-sized cell appended after the items (e.g. the unoptimized card). */
  trailingCard?: ReactNode;
}) {
  const columns = useElementColumns(scrollContainer, columnBreakpoints);
  const rows = useMemo(
    () => buildRows(sections, columns, getItemKey, trailingCard),
    [sections, columns, getItemKey, trailingCard]
  );
  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);

  // Each card is a listitem carrying its position in the full (virtualized)
  // list, so assistive tech announces "item N of totalItems" even though only
  // the visible window is in the DOM. Headers stay as plain text between items.
  const renderRow = (row: Row<Item>) => {
    if (row.kind === "header") {
      return row.node;
    }
    return (
      // Presentation so the listitems below read as direct children of the list
      // despite the layout wrappers between them.
      <div className={`grid gap-2 ${GRID_COLS[columns]}`} role="presentation">
        {row.items.map((item, i) => (
          <div
            aria-posinset={row.startIndex + i + 1}
            aria-setsize={totalItems}
            key={getItemKey(item)}
            role="listitem"
          >
            {renderItem(item)}
          </div>
        ))}
        {row.trailingCard === undefined ? null : (
          // An action cell (reveal hidden matches), not one of the enumerated
          // items, so it carries no aria-posinset/setsize.
          <div role="listitem">{row.trailingCard}</div>
        )}
      </div>
    );
  };

  // Render plainly when the list is short, or while the scroll element hasn't
  // mounted yet (isolated stories, or the frame before the viewport ref
  // resolves). Always render every row here — never a capped subset — so a
  // consumer without a scroll container still shows the whole catalog rather
  // than silently truncating it. In the picker the catalog loads after the
  // viewport mounts, so a large list reaches the windowed path below.
  if (!scrollContainer || totalItems <= VIRTUALIZE_THRESHOLD) {
    return (
      <div aria-label={label} className="space-y-2" role="list">
        {rows.map((row) => (
          <Fragment key={row.key}>{renderRow(row)}</Fragment>
        ))}
      </div>
    );
  }

  return (
    <VirtualizedRows
      cardRowHeight={cardRowHeight}
      gapPx={gapPx}
      headerHeight={headerHeight}
      label={label}
      renderRow={renderRow}
      rows={rows}
      scrollContainer={scrollContainer}
    />
  );
}

function VirtualizedRows<Item>({
  cardRowHeight,
  gapPx,
  headerHeight,
  label,
  renderRow,
  rows,
  scrollContainer,
}: {
  cardRowHeight: number;
  gapPx: number;
  headerHeight: number;
  label?: string;
  renderRow: (row: Row<Item>) => ReactNode;
  rows: Row<Item>[];
  scrollContainer: HTMLElement;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // Rows sit below this grid's own offset in the scroll content (a nav header
  // may precede them). Feed that offset to the virtualizer as scrollMargin so
  // row positions line up. Only this one grid lives in the scroll container, so
  // a resize of it or the viewport is the only thing that moves the rows.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }
    const update = () => {
      const gridTop = grid.getBoundingClientRect().top;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      setScrollMargin(
        Math.max(
          0,
          Math.round(gridTop - containerTop + scrollContainer.scrollTop)
        )
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(grid);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: (index) =>
      rows[index].kind === "header" ? headerHeight : cardRowHeight,
    gap: gapPx,
    getItemKey: (index) => rows[index].key,
    getScrollElement: () => scrollContainer,
    overscan: 4,
    scrollMargin,
  });

  return (
    <div
      aria-label={label}
      className="relative w-full"
      data-slot="virtualized-card-grid"
      ref={gridRef}
      role="list"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) {
          return null;
        }
        return (
          <div
            className="absolute top-0 left-0 w-full"
            data-index={virtualRow.index}
            key={virtualRow.key}
            ref={virtualizer.measureElement}
            role="presentation"
            style={{
              transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            {renderRow(row)}
          </div>
        );
      })}
    </div>
  );
}
